// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import { PLANTING_STATUS, STEP_NAME, TASK_STATUS, isPlantStep, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useGating, useUserContext } from '../../context/UserContext';
import { MenuItem } from '../../lib/base';
import { CheckCircleIcon, EditIcon, RedoIcon, UnpublishedIcon } from '../../lib/icons';
import {
  DASHBOARD_OVERDUE_TASKS_QUERY,
  DASHBOARD_QUERY,
  DASHBOARD_TASKS_COMPLETED_TODAY_QUERY,
} from '../../queries/DashboardQueries';
import { EDIT_TASK_GROUP_DATE_MUTATION } from '../../queries/EditTaskGroupDateMutation';
import { EDIT_TASK_MUTATION } from '../../queries/EditTaskQueries';
import { UNDO_TASK_COMPLETED_MUTATION } from '../../queries/UndoTaskCompletionMutation';
import type {
  ChangeTaskGroupStatusMutation,
  ChangeTaskGroupStatusMutationVariables,
  EditTaskGroupDateMutation,
  EditTaskGroupDateMutationVariables,
  UndoTaskCompletedMutation,
  UndoTaskCompletedMutationVariables,
} from '../../types/__generated__/graphql';
import { getTimeRanges } from '../../utils';
import { Options } from '../common/dropdown';
import EditTaskDateDialog from './EditTaskDateDialog';
import EditTaskNotesDialog from './EditTaskNotesDialog';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface Props {
  readonly taskIds: readonly number[];
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
  readonly useMoreIcon?: boolean;
  readonly taskStatus: string;
  readonly taskName: string;
  readonly taskTime: number;
  readonly canEditTaskDateOneDay: boolean;
}

export default function TaskGroupOptions({
  taskIds,
  taskTime,
  canEditTaskDateOneDay,
  onSuccess,
  useMoreIcon,
  taskStatus,
  taskName,
}: Props) {
  const { permissions } = useUserContext();
  const { canUseBasic } = useGating();
  const { timezone } = useUserContext();
  const { todayStart, todayEnd, sevenDaysFromTodayStart } = getTimeRanges(timezone);
  const [optionsClose, setOptionsClose] = React.useState(false);
  const [editNotesOpen, setEditNotesOpen] = React.useState(false);
  const [editTaskDateOpen, setEditTaskDateOpen] = React.useState(false);
  const [editTaskStatuses, { loading: editStatusLoading }] = useMutation<
    ChangeTaskGroupStatusMutation,
    ChangeTaskGroupStatusMutationVariables
  >(EDIT_TASK_MUTATION);
  const [editTaskGroupDate, { loading: editDateLoading }] = useMutation<
    EditTaskGroupDateMutation,
    EditTaskGroupDateMutationVariables
  >(EDIT_TASK_GROUP_DATE_MUTATION);
  const [undoCompleted, { loading: undoCompletionLoading }] = useMutation<
    UndoTaskCompletedMutation,
    UndoTaskCompletedMutationVariables
  >(UNDO_TASK_COMPLETED_MUTATION);

  React.useEffect(() => {
    setOptionsClose(false);
  });

  const editNotes = (
    <MenuItem
      key="edit-task-notes"
      onClick={() => {
        setEditNotesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Notes
    </MenuItem>
  );

  const complete = (
    <MenuItem
      key="complete-task"
      disabled={editStatusLoading || editDateLoading || undoCompletionLoading || !canUseBasic}
      onClick={() => {
        void editTaskStatuses({
          refetchQueries: [
            {
              query: DASHBOARD_TASKS_COMPLETED_TODAY_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                todayEnd: todayEnd.toString(),
                first: 25,
              },
            },
          ],
          variables: { ids: taskIds.map((t) => t) },
          update: (cache, { data }) => {
            const plantingsCreated = (data?.changeTaskStatuses?.plantings_created?.length ?? 0) > 0;
            const tasksCreated = (data?.changeTaskStatuses?.tasks_created?.length ?? 0) > 0;
            cache.modify({
              fields: {
                farm_report(existingReport, { DELETE }) {
                  return plantingsCreated ? DELETE : existingReport;
                },
                harvests(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantings(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                taskGroup(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                plantingGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                planting_groups(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantingTimeGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
              },
            });
          },
          onCompleted: () => {
            setOptionsClose(true);
            enqueueSnackbar('Completed task', { variant: 'success' });
            onSuccess(false);
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
      }}
      disableRipple
    >
      <CheckCircleIcon />
      Mark Complete
    </MenuItem>
  );
  const editDate = (
    <MenuItem
      key="edit-task-date"
      disabled={editDateLoading || undoCompletionLoading || editStatusLoading || !canUseBasic}
      onClick={() => {
        setEditTaskDateOpen(true);
      }}
      disableRipple
    >
      <EditIcon />
      Edit Task Date
    </MenuItem>
  );
  const editDateOneDay = (
    <MenuItem
      key="edit-task-date-one-day"
      disabled={editDateLoading || undoCompletionLoading || editStatusLoading || !canUseBasic}
      onClick={() => {
        void editTaskGroupDate({
          variables: {
            task_id: taskIds[0],
            new_time: utils.toNoonTime(dayjs.unix(taskTime).tz(timezone).add(1, 'day'), timezone).unix(),
          },
          onCompleted: (data) => {
            if (data !== undefined) {
              enqueueSnackbar('Updated task date', { variant: 'success' });
              onSuccess(true);
            } else {
              enqueueSnackbar('Something went wrong!', {
                variant: 'error',
                autoHideDuration: 10000,
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
            clientLogger.error({ title: 'new_task_error', message: errorIn.message });
          },
        });
      }}
      disableRipple
    >
      <RedoIcon />
      Move Task To Next Day
    </MenuItem>
  );
  const isPlantTask = isPlantStep(taskName);
  const undoTaskCompletionItem = (
    <MenuItem
      key="undo-task-completion"
      disabled={undoCompletionLoading || editStatusLoading || editDateLoading || isPlantTask || !canUseBasic}
      onClick={() => {
        void undoCompleted({
          variables: {
            task_ids: [...taskIds],
          },
          refetchQueries: [
            {
              query: DASHBOARD_TASKS_COMPLETED_TODAY_QUERY,
              variables: { todayStart: todayStart.toString(), todayEnd: todayEnd.toString(), first: 25 },
            },
            {
              query: DASHBOARD_OVERDUE_TASKS_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                first: 25,
              },
            },
            {
              query: DASHBOARD_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
              },
            },
          ],
          update: (cache, { data }) => {
            const updatedPlantings = data?.undoTaskCompletion?.updated_plantings ?? [];
            const plantingsNowPlanned = updatedPlantings.filter((p) => p.planting_status === PLANTING_STATUS.PLANNED);
            cache.modify({
              fields: {
                taskGroup(_, { DELETE }) {
                  // It's possible that some task groups can now be combined
                  return DELETE;
                },
                task_groups(_, { DELETE }) {
                  // It's possible that some task groups can now be combined
                  return DELETE;
                },
                plantingGroup(existingGroup, { DELETE }) {
                  // If some plantings are now planned, then it's possible for two or more
                  // existing planting groups can now be combined
                  return plantingsNowPlanned ? DELETE : existingGroup;
                },
                planting_groups(existingGroup, { DELETE }) {
                  return plantingsNowPlanned ? DELETE : existingGroup;
                },
              },
            });
          },
          onCompleted: (data) => {
            if (data !== undefined) {
              enqueueSnackbar('Task completion undone', { variant: 'success' });
              // Refetch the task group in case it can now be combined with other tasks
              // which now are all not completed but have the same time + crop + name
              onSuccess(true);
            } else {
              enqueueSnackbar('Something went wrong!', {
                variant: 'error',
                autoHideDuration: 10000,
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
      }}
      disableRipple
    >
      <UnpublishedIcon />
      {isPlantTask ? 'Undoing Plant Task Not Yet Supported' : 'Undo Task Completion'}
    </MenuItem>
  );

  const userCanCompleteTasks = permissions.can_complete_tasks;
  const options = [];
  const isNotCompleted = taskStatus !== TASK_STATUS.COMPLETED;
  if (userCanCompleteTasks) {
    options.push(isNotCompleted ? complete : undoTaskCompletionItem);
  }
  options.push(editNotes);
  const isNotHarvestTask = taskName !== STEP_NAME.HARVEST;
  if (isNotCompleted && userCanCompleteTasks && canEditTaskDateOneDay && isNotHarvestTask) {
    options.push(editDateOneDay);
  }
  if (isNotCompleted && userCanCompleteTasks && isNotHarvestTask) {
    options.push(editDate);
  }

  return (
    <>
      <Options useMoreIcon={useMoreIcon} close={optionsClose} options={options} />
      {editTaskDateOpen && (
        <EditTaskDateDialog
          isOpen={editTaskDateOpen}
          onClose={() => {
            setEditTaskDateOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditTaskDateOpen(false);
            setOptionsClose(true);
            onSuccess(true);
          }}
          taskId={taskIds[0]}
        />
      )}
      {editNotesOpen && (
        <EditTaskNotesDialog
          isOpen={editNotesOpen}
          onClose={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
          taskIds={taskIds}
        />
      )}
    </>
  );
}
